/**
 * Moteur de la simulation — PUR : un état, une action, un nouvel état. Aucun
 * I/O, aucun React, aucune horloge interne (`maintenant` est passé par
 * l'appelant) : la scène entière se rejoue dans un test.
 *
 * ─── CE QUE LE CANDIDAT FAIT, ET RIEN D'AUTRE (Kayla, 06-10-2026) ──────────
 * « Ajouter des personnes à leur file, puis lancer un flow, le flow se réalise,
 * puis envoyer le message à 50, puis créer une transaction. » Le moteur connaît
 * donc les gestes du POSTE, pas ceux d'une messagerie : ajouter un fan à sa
 * file, ouvrir une conversation, écrire, lancer un flow (et l'arrêter),
 * envoyer une transaction, rendre la main à Cyrano. Le flow, lui, avance tout
 * seul : l'écran pose un minuteur sur `prochainDelai` et joue `flow_avance` —
 * c'est la seule action qui ne vient pas du candidat.
 *
 * Plusieurs fans, UNE scène. Le fan de la scène est celui sur lequel il lance
 * son premier flow (ou, s'il écrit au lieu de lancer, celui à qui il écrit) :
 * c'est lui qui demandera le prix, paiera, et à qui on rendra Cyrano. Ce qu'il
 * écrit aux autres est gardé (Kayla lit tout), mais personne n'y répond.
 *
 * Règle de survie, inchangée depuis le 18-08 : quand le candidat patine, le
 * fan relance ; au bout de quelques faux pas l'étape est marquée ratée et la
 * scène avance quand même (`PATIENCE`). Un test qui coince ne dit rien de plus
 * qu'un test raté — et il faut qu'il atteigne la transaction.
 *
 * ⏱️ Chaque étape porte son DÉBUT et sa FIN (`marques`) : « combien de temps il
 * a mis » se lit étape par étape, pas seulement au total.
 */
import {
  APRES_LE_PAIEMENT,
  FANS,
  FAN_CEDE_A_50,
  FAN_INSISTE,
  OBJECTION_DU_FAN,
  PATIENCE,
  PLANCHER_DE_LA_VIDEO_CENTS,
  REPONSE_A_LA_FIN_DU_FLOW,
  VIDEO_A_30,
  VIDEO_A_50,
  alerteDeFinDeFlow,
  PLAFOND_DE_LA_FILE,
  fanParId,
  flowParCle,
  messagesDuFlow,
  offreParCle,
  fanAccepte,
  positionDeNegociation,
  mediaParId,
  proposeLaVideo,
  type Etape,
  type EtapeJouee,
  type PasDuFlow,
} from "./scenario";

// ─── Ce que le candidat peut faire ──────────────────────────────────────────

/**
 * D'où vient le texte qu'il envoie — la question de Kayla du 18-08 : « s'il a
 * exécuté une action script ou s'il a rédigé un message ». Deux réservoirs
 * depuis le 06-10 : les Scripts (onglet, ou bulle de « À faire maintenant ») et
 * Francis. Insérer PUIS retoucher n'est ni l'un ni l'autre, et c'est le cas le
 * plus intéressant : il a trouvé vite ET il se l'est approprié.
 */
export type SourceTexte =
  | "script"
  | "script_modifie"
  | "francis"
  | "francis_modifie"
  | "ecrit";

/** Par où il a lancé le flow : la barre « À faire maintenant », ou le menu
 *  « Flow ▾ ». */
export type ViaDuFlow = "a_faire" | "menu";

/** Par où est partie la transaction : le bouton $ (Vente), ou « Préparer la
 *  vente » de Francis, qui ouvre la même fenêtre pré-remplie. */
export type ViaDeLaVente = "vente" | "francis";

export type Action =
  | { type: "ajouter"; fanId: string }
  | { type: "ouvrir"; fanId: string }
  | { type: "dire"; fanId: string; texte: string; source?: SourceTexte }
  | { type: "lancer_flow"; fanId: string; flowCle: string; via: ViaDuFlow }
  /** Le flow envoie son pas suivant — le minuteur de l'écran, ou « Envoyer
   *  maintenant ». */
  | { type: "flow_avance" }
  | { type: "arreter_flow" }
  | {
      type: "transaction";
      fanId: string;
      /** Le produit CHOISI dans le catalogue — null s'il n'en a pas choisi.
       *  (Le nom du champ est celui du poste : `offer_key`.) */
      offreCle: string | null;
      prixCents: number;
      devise: string;
      /** Les médias du Vault à délivrer au paiement — LE média vendu doit y
       *  être (06-10-2026 : on vend un média, pas une formule). */
      medias: readonly string[];
      /** « Il a déjà payé » coché : rien ne part, la vente est enregistrée. */
      dejaPaye: boolean;
      via: ViaDeLaVente;
    }
  | { type: "rendre"; fanId: string };

// ─── Le fil ─────────────────────────────────────────────────────────────────

export interface Message {
  id: string;
  /**
   * 🪤 `cyrano` et `flow` ne sont pas le candidat. Ce que Cyrano a écrit avant
   * lui, et ce que le flow écrit pendant qu'il attend, s'affichent sous le nom
   * « Cyrano » — comme au poste. Les confondre avec « Toi », c'est lui
   * attribuer une conversation qu'il n'a pas tenue.
   */
  de: "fan" | "chatteur" | "cyrano" | "flow" | "systeme";
  texte: string;
  /** ms depuis le début du test (intro comprise). */
  a: number;
  /** Rendu spécial : le lien de paiement envoyé, le paiement reçu, ou
   *  l'alerte « à toi » de fin de flow. */
  bloc?: "transaction" | "paiement" | "alerte";
  /** Un média envoyé par le flow — vignette floutée. */
  media?: "video" | "image" | "vocal" | "gif";
  /** Ce que dit un vocal, quand la prod le sait. */
  transcription?: string;
  transaction?: {
    offreCle: string | null;
    prixCents: number;
    devise: string;
    /** Les titres des médias délivrés au paiement. */
    medias: readonly string[];
  };
}

/** Un flow qui tourne (ou a tourné) sur le fan de la scène. */
export interface RunDeFlow {
  fanId: string;
  flowCle: string;
  /** Pas du programme déjà joués. */
  pas: number;
  /** Messages du FLOW déjà partis — c'est ce que compte le badge. */
  envoyes: number;
  /** Messages du flow au total. */
  total: number;
  statut: "en_cours" | "termine" | "arrete";
}

/**
 * Un geste du candidat, tel que Kayla le relira ligne à ligne : à quelle
 * étape, ce qu'il a fait, sur quoi, d'où venait le texte. On garde TOUTES les
 * tentatives, y compris celles qui ratent.
 */
export interface Geste {
  etape: EtapeJouee;
  type: Exclude<Action["type"], "flow_avance">;
  /** Le texte envoyé ; pour un geste qui n'est pas du texte, ce sur quoi il
   *  porte (« Lucas », « script 1 · Lucas », « Vidéo solo · 50 €… »). */
  texte: string;
  /** La provenance d'un texte, le chemin d'un flow ou d'une vente — null
   *  pour le reste. */
  source: string | null;
  /** ms depuis le début du test. */
  a: number;
  /** Ce geste-là satisfaisait-il l'étape en cours ? */
  reussi: boolean;
}

/** Le verdict d'UNE étape, pour la note. */
export interface Evenement {
  etape: EtapeJouee;
  /** true = il l'a fait ; false = raté, ou la scène a avancé sans lui. */
  reussi: boolean;
  a: number;
  detail?: Record<string, number | string>;
}

/** Le début et la fin d'une étape — « combien de temps il a mis ». */
export interface Marque {
  etape: EtapeJouee;
  debut: number;
  /** null tant que l'étape n'est pas close. */
  fin: number | null;
}

/** Le média et le prix conclus dans la conversation. */
export interface Accord {
  /** Le MÉDIA conclu (`VAULT`). */
  mediaId: string;
  prixCents: number;
}

export interface Etat {
  etape: Etape;
  /** Les fans de « Ma file », dans l'ordre où il les a ajoutés. */
  file: string[];
  /** La conversation ouverte au centre. */
  ouvert: string | null;
  /** Le fil de chaque fan. */
  fils: Record<string, Message[]>;
  /** Le fan de la scène — fixé au premier flow (ou au premier faux pas du
   *  temps `flow`). */
  sceneFan: string | null;
  run: RunDeFlow | null;
  /** Messages qu'il a écrits au fan PENDANT que son flow tournait. */
  ecritsPendantLeFlow: number;
  /** Faux pas déjà faits sur l'étape courante (cf. `PATIENCE`). */
  faux: number;
  /**
   * Ce qu'il a écrit au fan de la scène pendant la VENTE (proposer +
   * négocier) — c'est là qu'on cherche s'il a décrit le média et s'il l'a
   * fait sentir « fait pour lui » (Kayla, 06-10-2026).
   */
  messagesDeVente: string[];
  /** Combien de fois il a tenu 50 € face à l'objection (le fan cède à la 2e). */
  tenu: number;
  /** Ce qui a été CONCLU dans la conversation : le média et son prix. La
   *  transaction doit le reprendre à l'identique. */
  accord: Accord | null;
  evenements: Evenement[];
  /** Tout ce qu'il a écrit — c'est ça que Kayla lit. */
  sesMessages: string[];
  gestes: Geste[];
  marques: Marque[];
  fini: boolean;
}

let compteur = 0;
function nouvelId(): string {
  compteur += 1;
  return `m${compteur}`;
}

/** Remet le compteur d'ids à zéro — pour que les tests soient déterministes. */
export function reinitialiserIds(): void {
  compteur = 0;
}

/** `maintenant` = l'instant où la simulation s'ouvre (après l'intro). */
export function etatInitial(maintenant = 0): Etat {
  const fils: Record<string, Message[]> = {};
  for (const f of FANS) {
    fils[f.id] = f.fil.map((l) => ({ id: nouvelId(), de: l.de, texte: l.texte, a: maintenant }));
  }
  return {
    etape: "file",
    file: [],
    ouvert: null,
    fils,
    sceneFan: null,
    run: null,
    ecritsPendantLeFlow: 0,
    faux: 0,
    messagesDeVente: [],
    tenu: 0,
    accord: null,
    evenements: [],
    sesMessages: [],
    gestes: [],
    marques: [{ etape: "file", debut: maintenant, fin: null }],
    fini: false,
  };
}

// ─── Le flow simulé ─────────────────────────────────────────────────────────

/** Les pas d'un run : ceux du flow, puis le fan qui demande le prix. */
export function programmeDuRun(run: RunDeFlow): readonly PasDuFlow[] {
  const flow = flowParCle(run.flowCle);
  return [
    ...(flow?.pas ?? []),
    { de: "fan", texte: REPONSE_A_LA_FIN_DU_FLOW, apresMs: 2500, indicateur: "il écrit…" },
    ...(flow ? [{ de: "alerte" as const, texte: alerteDeFinDeFlow(nomDe(run.fanId), flow), apresMs: 400 }] : []),
  ];
}

/**
 * Dans combien de temps le flow joue son pas suivant — `null` quand rien ne
 * tourne. L'écran pose son minuteur là-dessus ; le moteur ne compte jamais le
 * temps lui-même.
 */
export function prochainDelai(etat: Etat): number | null {
  const run = etat.run;
  if (!run || run.statut === "arrete") return null;
  const programme = programmeDuRun(run);
  return run.pas < programme.length ? programme[run.pas]!.apresMs : null;
}

/** Ce que le fil affiche pendant l'attente du pas suivant (« elle écrit… »,
 *  « vidéo en cours d'envoi… ») — null quand rien ne tourne. */
export function indicateurDuFlow(etat: Etat): string | null {
  const run = etat.run;
  if (!run || run.statut === "arrete") return null;
  const pas = programmeDuRun(run)[run.pas];
  if (!pas || pas.de === "alerte") return null;
  return pas.indicateur ?? (pas.de === "flow" ? "elle écrit…" : "il écrit…");
}

/** Le prochain message que le FLOW enverra — pour « Envoyer maintenant ». */
export function prochainMessageDuFlow(etat: Etat): string | null {
  const run = etat.run;
  if (!run || run.statut !== "en_cours") return null;
  const suivant = programmeDuRun(run)
    .slice(run.pas)
    .find((p) => p.de === "flow");
  if (!suivant) return null;
  return suivant.texte || (suivant.media === "video" ? "une vidéo" : suivant.media === "vocal" ? "un vocal" : "un média");
}

/**
 * Pourquoi ce flow est grisé pour ce fan — `null` s'il se lance.
 *
 * Comme au poste (`flow/creneaux.motifIndisponible`), un flow indisponible se
 * montre GRISÉ avec sa raison, jamais caché. « déjà reçu » est la vraie raison
 * du poste : un ancien fan a déjà eu « script 1 ». « pas dans ce test » est la
 * seule raison inventée — un seul flow tourne par passage, sinon la scène
 * aurait deux fans à suivre et la note ne saurait plus lequel juger.
 */
export function motifIndisponible(etat: Etat, fanId: string, flowCle: string): string | null {
  const fan = fanParId(fanId);
  const dejaRecu =
    flowCle === "script_1" && (fan?.nouveau === false || etat.run?.fanId === fanId);
  if (dejaRecu) return "déjà reçu";
  if (etat.run !== null || (etat.etape !== "file" && etat.etape !== "flow")) {
    return "pas dans ce test";
  }
  return null;
}

// ─── Les petits gestes internes (sur une COPIE, jamais sur l'état reçu) ─────

function copier(etat: Etat): Etat {
  const fils: Record<string, Message[]> = {};
  for (const [k, v] of Object.entries(etat.fils)) fils[k] = [...v];
  return {
    ...etat,
    file: [...etat.file],
    fils,
    run: etat.run ? { ...etat.run } : null,
    evenements: [...etat.evenements],
    sesMessages: [...etat.sesMessages],
    messagesDeVente: [...etat.messagesDeVente],
    accord: etat.accord ? { ...etat.accord } : null,
    gestes: [...etat.gestes],
    marques: etat.marques.map((m) => ({ ...m })),
  };
}

function poser(e: Etat, fanId: string, m: Omit<Message, "id">): void {
  (e.fils[fanId] ??= []).push({ id: nouvelId(), ...m });
}

function consigner(e: Etat, g: Omit<Geste, "etape">): void {
  e.gestes.push({ etape: e.etape as EtapeJouee, ...g });
}

function clore(e: Etat, reussi: boolean, a: number, detail?: Evenement["detail"]): void {
  e.evenements.push({
    etape: e.etape as EtapeJouee,
    reussi,
    a,
    ...(detail === undefined ? {} : { detail }),
  });
}

/** Ferme l'étape courante et ouvre la suivante — la marque de fin part ici. */
function passerA(e: Etat, suivante: Etape, a: number): void {
  const courante = e.marques[e.marques.length - 1];
  if (courante && courante.fin === null) courante.fin = a;
  e.etape = suivante;
  e.faux = 0;
  if (suivante === "fini") {
    e.fini = true;
    return;
  }
  e.marques.push({ etape: suivante, debut: a, fin: null });
}

function nomDe(fanId: string): string {
  return fanParId(fanId)?.nom ?? fanId;
}

function euros(cents: number, devise: string): string {
  const n = cents / 100;
  return `${Number.isInteger(n) ? n : n.toFixed(2)} ${devise === "EUR" ? "€" : devise}`;
}

/**
 * Un faux pas sur l'étape courante : le fan relance, et au bout de sa
 * patience la scène avance sans le candidat. Rend `true` si elle a avancé.
 */
function fauxPas(e: Etat, fanId: string, a: number): boolean {
  const etape = e.etape as EtapeJouee;
  const patience = PATIENCE[etape];
  if (!patience) return false;
  e.faux += 1;
  if (e.faux < patience.essais) {
    const relance = patience.relances[Math.min(e.faux, patience.relances.length) - 1];
    if (relance) poser(e, fanId, { de: "fan", texte: relance, a });
    return false;
  }
  if (patience.avance) poser(e, fanId, { de: "fan", texte: patience.avance, a });
  clore(e, false, a, { avance: "forcee" });
  // Le fan qui se décide tout seul (« j'le prends à 50 ») conclut LUI-MÊME :
  // c'est l'accord que la transaction doit reprendre.
  if (etape === "proposer" || etape === "negocier") {
    e.accord = { mediaId: VIDEO_A_50.id, prixCents: VIDEO_A_50.prixCents };
  }
  const suivante: Record<EtapeJouee, Etape> = {
    file: "flow",
    flow: "proposer",
    flow_en_cours: "proposer",
    proposer: "transaction",
    negocier: "transaction",
    transaction: "rendre",
    rendre: "fini",
  };
  passerA(e, suivante[etape], a);
  return true;
}

/** Le temps `file` se ferme : réussi si les DEUX nouveaux sont dans sa file. */
function cloreLaFile(e: Etat, a: number): void {
  const nouveaux = FANS.filter((f) => f.nouveau);
  const dedans = nouveaux.filter((f) => e.file.includes(f.id)).length;
  const anciens = e.file.filter((id) => fanParId(id)?.nouveau === false).length;
  clore(e, dedans === nouveaux.length, a, { nouveaux: dedans, sur: nouveaux.length, anciens });
  passerA(e, "flow", a);
}

// ─── Les actions ────────────────────────────────────────────────────────────

function ajouter(e: Etat, fanId: string, a: number): void {
  const fan = fanParId(fanId);
  if (!fan || e.file.includes(fanId) || e.file.length >= PLAFOND_DE_LA_FILE) return;
  e.file.push(fanId);
  consigner(e, { type: "ajouter", texte: fan.nom, source: null, a, reussi: fan.nouveau });
  if (e.etape === "file" && FANS.filter((f) => f.nouveau).every((f) => e.file.includes(f.id))) {
    cloreLaFile(e, a);
  }
}

function ouvrir(e: Etat, fanId: string, a: number): void {
  const fan = fanParId(fanId);
  if (!fan || !e.file.includes(fanId) || e.ouvert === fanId) return;
  e.ouvert = fanId;
  consigner(e, { type: "ouvrir", texte: fan.nom, source: null, a, reussi: fan.nouveau });
  // Ouvrir une conversation, c'est avoir fini de remplir sa file — avec ou
  // sans les deux nouveaux. Sans cette porte, un candidat qui n'en ajoute qu'un
  // resterait à l'étape 1 pour toujours.
  if (e.etape === "file") cloreLaFile(e, a);
}

function lancerFlow(
  e: Etat,
  action: Extract<Action, { type: "lancer_flow" }>,
  a: number,
): void {
  const fan = fanParId(action.fanId);
  const flow = flowParCle(action.flowCle);
  if (!fan || !flow || !e.file.includes(fan.id)) return;
  if (motifIndisponible(e, fan.id, flow.cle) !== null) return;
  if (e.etape === "file") cloreLaFile(e, a);

  const reussi = flow.cle === "script_1" && fan.nouveau;
  consigner(e, { type: "lancer_flow", texte: `${flow.nom} · ${fan.nom}`, source: action.via, a, reussi });
  clore(e, reussi, a, { flow: flow.nom, fan: fan.nom, nouveau: fan.nouveau ? 1 : 0, via: action.via });
  e.sceneFan = fan.id;
  e.run = {
    fanId: fan.id,
    flowCle: flow.cle,
    pas: 0,
    envoyes: 0,
    total: messagesDuFlow(flow),
    statut: "en_cours",
  };
  e.ecritsPendantLeFlow = 0;
  passerA(e, "flow_en_cours", a);
}

function flowAvance(e: Etat, a: number): void {
  const run = e.run;
  if (!run || run.statut === "arrete") return;
  const programme = programmeDuRun(run);
  const pas = programme[run.pas];
  if (!pas) return;
  if (pas.de === "alerte") {
    poser(e, run.fanId, { de: "systeme", texte: pas.texte, a, bloc: "alerte" });
  } else {
    poser(e, run.fanId, {
      de: pas.de,
      texte: pas.texte,
      a,
      ...(pas.media ? { media: pas.media } : {}),
      ...(pas.transcription ? { transcription: pas.transcription } : {}),
    });
  }
  run.pas += 1;
  if (pas.de === "flow") {
    run.envoyes += 1;
    // Le dernier message du flow est parti : « Flow terminé ». L'étape se
    // ferme ICI, pas après la question du fan — s'il annonce le prix dans la
    // seconde qui suit, c'est déjà le temps `proposer`.
    if (run.envoyes >= run.total) {
      run.statut = "termine";
      if (e.etape === "flow_en_cours") {
        clore(e, e.ecritsPendantLeFlow === 0, a, { ecrits: e.ecritsPendantLeFlow });
        passerA(e, "proposer", a);
      }
    }
  }
}

function arreterFlow(e: Etat, a: number): void {
  const run = e.run;
  if (!run || run.statut !== "en_cours") return;
  run.statut = "arrete";
  const flow = flowParCle(run.flowCle);
  consigner(e, { type: "arreter_flow", texte: flow?.nom ?? run.flowCle, source: null, a, reussi: false });
  // Le fan n'attend pas la suite qui ne viendra pas : il demande le prix.
  poser(e, run.fanId, { de: "fan", texte: REPONSE_A_LA_FIN_DU_FLOW, a });
  run.pas = programmeDuRun(run).length;
  if (e.etape === "flow_en_cours") {
    clore(e, false, a, { arrete: 1, ecrits: e.ecritsPendantLeFlow });
    passerA(e, "proposer", a);
  }
}

/**
 * La négociation se conclut : le fan accepte la position du candidat, et
 * l'accord est posé. `valeur` est réussie quand il a tenu, baissé à 40 € au
 * plus bas, ou changé de média — ratée quand il a bradé la vidéo proposée à 50.
 */
function conclure(
  e: Etat,
  fanId: string,
  accord: Accord,
  issue: "tenu" | "baisse" | "autre" | "brade",
  a: number,
): void {
  e.accord = accord;
  poser(e, fanId, {
    de: "fan",
    texte: issue === "tenu" ? FAN_CEDE_A_50 : fanAccepte(accord),
    a,
  });
  clore(e, issue !== "brade", a, { issue, mediaId: accord.mediaId, prixCents: accord.prixCents });
  passerA(e, "transaction", a);
}

function dire(e: Etat, action: Extract<Action, { type: "dire" }>, a: number): void {
  const { fanId, texte } = action;
  if (!fanParId(fanId) || !e.file.includes(fanId)) return;
  const source = action.source ?? "ecrit";
  poser(e, fanId, { de: "chatteur", texte, a });
  e.sesMessages.push(texte);

  const surLaScene = fanId === e.sceneFan;
  switch (e.etape) {
    case "flow": {
      // Il écrit au lieu de lancer le flow : ce fan devient celui de la scène
      // (c'est à lui qu'il a choisi de parler), et il relance.
      consigner(e, { type: "dire", texte, source, a, reussi: false });
      e.sceneFan = fanId;
      fauxPas(e, fanId, a);
      return;
    }
    case "flow_en_cours": {
      const run = e.run;
      if (run && run.fanId === fanId && run.statut === "en_cours") e.ecritsPendantLeFlow += 1;
      consigner(e, { type: "dire", texte, source, a, reussi: false });
      return;
    }
    case "proposer": {
      const reussi = surLaScene && proposeLaVideo(texte);
      consigner(e, { type: "dire", texte, source, a, reussi });
      if (!surLaScene) return;
      e.messagesDeVente.push(texte);
      if (reussi) {
        clore(e, true, a, { source });
        // Il ne dit pas oui : il trouve ça cher (Kayla, 06-10-2026).
        for (const t of OBJECTION_DU_FAN) poser(e, fanId, { de: "fan", texte: t, a });
        passerA(e, "negocier", a);
        return;
      }
      fauxPas(e, fanId, a);
      return;
    }
    case "negocier": {
      if (!surLaScene) {
        consigner(e, { type: "dire", texte, source, a, reussi: false });
        return;
      }
      e.messagesDeVente.push(texte);
      const position = positionDeNegociation(texte);
      if (position === null) {
        consigner(e, { type: "dire", texte, source, a, reussi: false });
        fauxPas(e, fanId, a);
        return;
      }
      if (position.genre === "refus" || (position.genre === "meme" && position.prixCents >= VIDEO_A_50.prixCents)) {
        // Il TIENT 50. Le fan insiste une fois, puis cède.
        consigner(e, { type: "dire", texte, source, a, reussi: true });
        e.tenu += 1;
        if (e.tenu < 2) {
          poser(e, fanId, { de: "fan", texte: FAN_INSISTE, a });
          return;
        }
        conclure(e, fanId, { mediaId: VIDEO_A_50.id, prixCents: VIDEO_A_50.prixCents }, "tenu", a);
        return;
      }
      const accord: Accord =
        position.genre === "autre"
          ? { mediaId: VIDEO_A_30.id, prixCents: position.prixCents }
          : { mediaId: VIDEO_A_50.id, prixCents: position.prixCents };
      const issue =
        position.genre === "autre" ? "autre" : position.prixCents >= PLANCHER_DE_LA_VIDEO_CENTS ? "baisse" : "brade";
      consigner(e, { type: "dire", texte, source, a, reussi: issue !== "brade" });
      conclure(e, fanId, accord, issue, a);
      return;
    }
    case "transaction":
    case "rendre": {
      consigner(e, { type: "dire", texte, source, a, reussi: false });
      if (surLaScene) fauxPas(e, fanId, a);
      return;
    }
    default:
      consigner(e, { type: "dire", texte, source, a, reussi: false });
  }
}

function transaction(
  e: Etat,
  action: Extract<Action, { type: "transaction" }>,
  a: number,
): void {
  const { fanId } = action;
  if (!fanParId(fanId) || !e.file.includes(fanId)) return;
  const offre = offreParCle(action.offreCle);
  const libelle = `${offre?.nom ?? "sans produit"} · ${euros(action.prixCents, action.devise)}${
    action.dejaPaye ? " · déjà payé" : ""
  }`;
  const surLaScene = fanId === e.sceneFan;
  const aSonTour =
    surLaScene && (e.etape === "proposer" || e.etape === "negocier" || e.etape === "transaction");

  if (!aSonTour) {
    poser(e, fanId, {
      de: "systeme",
      texte: "Lien non envoyé : ce n'est pas encore le moment dans ce test.",
      a,
    });
    consigner(e, { type: "transaction", texte: libelle, source: action.via, a, reussi: false });
    return;
  }

  // Le lien part SANS que l'offre ait été annoncée : on le prend (c'est une
  // vente), mais le temps « message à 50 » est marqué raté.
  if (e.etape === "proposer") {
    clore(e, false, a, { sansMessage: 1 });
    passerA(e, "transaction", a);
  }
  // Le lien part en pleine négociation : c'est le lien qui dit sa position —
  // la même vidéo à ce prix, ou la plus petite (c'est le média DÉLIVRÉ qui le
  // dit). L'accord est ce lien.
  if (e.etape === "negocier" && !action.dejaPaye) {
    const autre = action.medias.includes(VIDEO_A_30.id) && !action.medias.includes(VIDEO_A_50.id);
    const issue = autre ? "autre" : action.prixCents >= PLANCHER_DE_LA_VIDEO_CENTS ? "baisse" : "brade";
    e.accord = { mediaId: autre ? VIDEO_A_30.id : VIDEO_A_50.id, prixCents: action.prixCents };
    clore(e, issue !== "brade", a, {
      issue,
      mediaId: e.accord.mediaId,
      prixCents: action.prixCents,
      sansReponse: 1,
    });
    passerA(e, "transaction", a);
  }

  if (action.dejaPaye) {
    // « Il a déjà payé » enregistre une vente encaissée AILLEURS : rien ne
    // part au fan. Ici il n'a rien payé — c'est un faux pas, que le fan relève.
    poser(e, fanId, {
      de: "systeme",
      texte: "Vente enregistrée sans lien : le fan n'a rien reçu.",
      a,
    });
    consigner(e, { type: "transaction", texte: libelle, source: action.via, a, reussi: false });
    fauxPas(e, fanId, a);
    return;
  }

  const detail = {
    offreCle: action.offreCle ?? "",
    prixCents: action.prixCents,
    devise: action.devise,
    medias: action.medias.join(","),
    via: action.via,
  };
  consigner(e, { type: "transaction", texte: libelle, source: action.via, a, reussi: true });
  poser(e, fanId, {
    de: "chatteur",
    texte: "",
    a,
    bloc: "transaction",
    transaction: {
      offreCle: action.offreCle,
      prixCents: action.prixCents,
      devise: action.devise,
      medias: action.medias.map((id) => mediaParId(id)?.titre ?? id),
    },
  });
  // Le fan paie — sans vrai lien ni vraie commande : c'est une simulation.
  poser(e, fanId, {
    de: "systeme",
    texte: `✅ Payé ${euros(action.prixCents, action.devise)}`,
    a,
    bloc: "paiement",
  });
  for (const t of APRES_LE_PAIEMENT) poser(e, fanId, { de: "fan", texte: t, a });
  clore(e, true, a, detail);
  passerA(e, "rendre", a);
}

function rendre(e: Etat, fanId: string, a: number): void {
  if (!fanParId(fanId) || !e.file.includes(fanId)) return;
  const reussi = e.etape === "rendre" && fanId === e.sceneFan;
  consigner(e, { type: "rendre", texte: nomDe(fanId), source: null, a, reussi });
  if (!reussi) {
    poser(e, fanId, {
      de: "systeme",
      texte: "Cyrano ne reprend pas encore : la vente n'est pas finie.",
      a,
    });
    return;
  }
  poser(e, fanId, { de: "systeme", texte: "Cyrano a repris la conversation.", a });
  clore(e, true, a);
  passerA(e, "fini", a);
}

/**
 * Joue une action. Rend le nouvel état — jamais l'ancien muté.
 *
 * `maintenant` = ms depuis le début du test, fourni par l'appelant (l'écran
 * passe une horloge, les tests passent des entiers).
 */
export function jouer(etat: Etat, action: Action, maintenant: number): Etat {
  if (etat.fini) return etat;
  const e = copier(etat);
  switch (action.type) {
    case "ajouter":
      ajouter(e, action.fanId, maintenant);
      break;
    case "ouvrir":
      ouvrir(e, action.fanId, maintenant);
      break;
    case "lancer_flow":
      lancerFlow(e, action, maintenant);
      break;
    case "flow_avance":
      flowAvance(e, maintenant);
      break;
    case "arreter_flow":
      arreterFlow(e, maintenant);
      break;
    case "dire":
      dire(e, action, maintenant);
      break;
    case "transaction":
      transaction(e, action, maintenant);
      break;
    case "rendre":
      rendre(e, action.fanId, maintenant);
      break;
  }
  return e;
}
